import React, { useRef, useEffect, useState } from "react";
import { Paper, Button, Typography } from "@material-ui/core";
import { useFormik } from "formik";
import * as dayjs from "dayjs";
import useStyles from "@/styles/alarmStyle";
import SearchInput from "@/components/SearchInput";
import { AlarmHistoryListDatatable } from "@/helpers/alarmHistoryListDatatable";
import { useTranslation } from "@/langs/useTranslation";
import { toast } from "@/apis/request";
import { FormItem } from "@/components/Form";
import AlarmRuleList from "@/components/FormFields/AlarmRuleList";
import TagList from "@/components/FormFields/TagList";
import DateRangePicker from "@/components/DateRangePicker";
import {
  resetAlarmDeviceApi,
  deleteAlarmDeviceApi
} from "@/apis/apiCollection";
import modalConfirm from "@/components/Modal/modalConfirm";
import { IconFile } from "@/components/Icon";
import { useGlobalState } from "@/GlobalProvider";
import ProcessStatus from "@/components/FormFields/ProcessStatus";
import DeviceGroups from "@/components/FormFields/DeviceGroups/DeviceGroups";
import AlarmTemplateList from "@/components/FormFields/AlarmTemplateList";
import HasPermission from "@/components/commons/HasPermission";
import CollapsePanel, {
  CollapseItem,
  CollapseTrigger
} from "@/components/CollapsePanel";

const AlarmHistoryList = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const tableRef = useRef(null);
  const selectedRowsRef = useRef([]);
  const isReset = useRef(true);
  const [total, setTotal] = useState(0);

  const initialValues = {
    template: undefined,
    rule: undefined,
    status: undefined,
    group: undefined,
    tag: undefined,
    date: [
      dayjs().subtract(7, "d").startOf("d").valueOf(),
      dayjs().endOf("d").valueOf()
    ],
    keywords: ""
  };

  const { currentCompanyId } = useGlobalState();

  const { values, handleChange, handleSubmit, handleReset } = useFormik({
    initialValues,
    onSubmit: () => {
      handleReloadDataTable();
    }
  });

  const getSearchParams = () => {
    const formValues = isReset.current ? initialValues : values;
    const date = formValues.date;
    return {
      alarmTemplateId: formValues.template,
      alarmPolicyOptionId: formValues.rule,
      status: formValues.status,
      groupIds: formValues.group,
      tagIds: formValues.tag,
      triggerDateStart: date[0] ? dayjs(date[0]).valueOf() : undefined,
      triggerDateEnd: date[1] ? dayjs(date[1]).valueOf() : undefined,
      keywords: formValues.keywords
    };
  };

  const reloadTableWithGroupId = () => {
    isReset.current = false;
    tableRef.current?.reload();
  };

  const resetTableWithGroupId = () => {
    isReset.current = true;
    tableRef.current?.reload();
  };

  const handleReloadDataTable = reset => {
    if (reset) {
      resetTableWithGroupId();
    } else {
      reloadTableWithGroupId();
    }
  };

  const resetQuery = () => {
    handleReset();
    handleReloadDataTable(true);
  };

  const getSelectedRows = () => {
    const selectedIds = tableRef.current.getSelected();
    const data = tableRef.current?.api().data().toArray();
    const selectedRows = data.filter(item => selectedIds.includes(item.id));
    if (selectedRows && selectedRows.length) {
      selectedRowsRef.current = selectedRows;
      return selectedRows;
    }
    toast.info(t({ defaultMessage: "未选择任何设备" }));
  };

  const handleLoaded = json => {
    setTotal(json.recordsTotal);
  };

  const doReset = async rows => {
    if (!Array.isArray(rows)) {
      rows = [rows];
    }
    await resetAlarmDeviceApi({
      resetDevices: rows.map(row => ({
        deviceId: row.deviceId,
        alarmPolicyId: row.alarmPolicyId
      }))
    });
    handleReloadDataTable();
    toast.success(t({ defaultMessage: "操作成功" }));
  };

  const doDelete = async rows => {
    if (!Array.isArray(rows)) {
      rows = [rows];
    }
    await deleteAlarmDeviceApi({
      alarmHistoryIds: rows.map(row => row.id)
    });
    handleReloadDataTable();
    toast.success(t({ defaultMessage: "删除成功" }));
  };

  const handleAlarmReset = row => {
    if (row.alarmPolicyOptionName === "App Crash Detection") {
      modalConfirm({
        title: t({ defaultMessage: "重启提醒" }),
        content: t({
          defaultMessage: "是否确认重启？"
        }),
        onConfirm: () => {
          return doReset(row);
        }
      });
    } else {
      modalConfirm({
        title: t({ defaultMessage: "重置提醒" }),
        content: t({
          defaultMessage: "是否确认重置该报警历史？"
        }),
        onConfirm: () => {
          return doReset(row);
        }
      });
    }
  };

  const handleAlarmDelete = row => {
    modalConfirm({
      title: t({ defaultMessage: "删除提醒" }),
      content: t({
        defaultMessage: "是否确认删除该报警历史？"
      }),
      onConfirm: () => {
        return doDelete(row);
      }
    });
  };

  const handleBatchAlarmReset = () => {
    if (getSelectedRows()) {
      modalConfirm({
        title: t({ defaultMessage: "重置提醒" }),
        content: t({
          defaultMessage: "是否确认重置报警历史？"
        }),
        onConfirm: () => {
          return doReset(selectedRowsRef.current);
        }
      });
    }
  };

  const handleBatchAlarmDelete = () => {
    if (getSelectedRows()) {
      modalConfirm({
        title: t({ defaultMessage: "删除提醒" }),
        content: t({
          defaultMessage: "是否确认删除报警历史？"
        }),
        onConfirm: () => {
          return doDelete(selectedRowsRef.current);
        }
      });
    }
  };

  useEffect(() => {
    resetQuery();
  }, [currentCompanyId]);

  return (
    <div className={classes.main}>
      <Paper elevation={0}>
        <form onSubmit={handleSubmit}>
          <div className={classes.tableHeader}>
            <div className={classes.tableTop}>
              <div>
                <Typography variant="h5" component="span">
                  {t({ defaultMessage: "报警历史记录" })}
                </Typography>
                <Typography
                  variant="body2"
                  color="primary"
                  component="span"
                  className={classes.subTitle}
                >
                  <IconFile size={15} />
                  {t({ defaultMessage: "{num}条" }, { num: total })}
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  component="span"
                  className={classes.subTitle}
                >
                  {t({ defaultMessage: "仅显示近7天的历史纪录" })}
                </Typography>
              </div>
              <div className={classes.rightAction}>
                <SearchInput
                  placeholder={t({ defaultMessage: "搜索" })}
                  name="keywords"
                  icon={null}
                  value={values.keywords}
                  onChange={handleChange}
                />
              </div>
            </div>
            <CollapsePanel>
              <div className={classes.tableQueryForm}>
                <FormItem
                  label={t({ defaultMessage: "报警模板" })}
                  className={classes.formItem}
                >
                  <AlarmTemplateList
                    name="template"
                    value={values.template}
                    onChange={handleChange}
                  />
                </FormItem>
                <FormItem
                  label={t({ defaultMessage: "报警规则" })}
                  className={classes.formItem}
                >
                  <AlarmRuleList
                    name="rule"
                    value={values.rule}
                    onChange={handleChange}
                  />
                </FormItem>
                <FormItem
                  label={t({ defaultMessage: "处理状态" })}
                  className={classes.formItem}
                >
                  <ProcessStatus
                    name="status"
                    value={values.status}
                    onChange={handleChange}
                  />
                </FormItem>
                <FormItem
                  label={t({ defaultMessage: "门店" })}
                  className={classes.formItem}
                >
                  <DeviceGroups
                    name="group"
                    value={values.group}
                    onChange={handleChange}
                  />
                </FormItem>
                <CollapseItem>
                  <FormItem
                    label={t({ defaultMessage: "标签" })}
                    className={classes.formItem}
                  >
                    <TagList
                      name="tag"
                      value={values.tag}
                      onChange={handleChange}
                    />
                  </FormItem>
                  <FormItem
                    label={t({ defaultMessage: "触发时间" })}
                    className={classes.formItem}
                  >
                    <DateRangePicker
                      name="date"
                      value={values.date}
                      onChange={handleChange}
                    />
                  </FormItem>
                </CollapseItem>
                <FormItem className={classes.formItem}>
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    style={{ marginRight: 8 }}
                  >
                    {t({ defaultMessage: "查询" })}
                  </Button>
                  <Button variant="outlined" onClick={resetQuery}>
                    {t({ defaultMessage: "重置" })}
                  </Button>
                  <CollapseTrigger />
                </FormItem>
              </div>
            </CollapsePanel>
          </div>
        </form>
        <AlarmHistoryListDatatable
          ref={tableRef}
          getSearchParams={getSearchParams}
          onReset={handleAlarmReset}
          onDelete={handleAlarmDelete}
          onLoaded={handleLoaded}
          footer={
            <HasPermission permission="alarm_history_alarm_delete">
              <Button
                variant="outlined"
                color="primary"
                onClick={handleBatchAlarmReset}
              >
                {t({ defaultMessage: "批量重置" })}
              </Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleBatchAlarmDelete}
              >
                {t({ defaultMessage: "批量删除" })}
              </Button>
            </HasPermission>
          }
        />
      </Paper>
    </div>
  );
};

export default AlarmHistoryList;
